import { isActionOf } from 'typesafe-actions'
import { User } from 'oidc-client-ts'
import { loginAsync, loginStart, logout } from '../../actions/app/e3DGrpAuth.actions'
import { logger } from '../../../utils/dccLogger'
import getOAuthClient, { logout as authServiceLogout, setAuthToken, isAuthenticated } from '../../../services/e3DAuthService'
import { Middleware } from 'redux'
import { createNotification } from '../../actions/app/notification.actions'
import { clearPersistedE3DGrpAuth, persistE3DGrpAuth, getPersistedE3DGrpAuth } from '../../persistLocalStorageE3DAuthState'
import is3DExhibitionEnabledSelector from '../../selectors/feature-scope/is3DExhibitionEnabledSelector'
import i18n from 'i18next'
import history from '../../../history'
import { fetchEntryData } from '../../actions/app/entryData.actions'
import entryDataSelector from '../../selectors/entryDataSelector'
import sessionIdSelector from '../../selectors/sessionIdSelector'
import { fetchCarlineData } from '../../actions/app/carlineData.actions'
import { showE3DSharingDashboard, setShouldAutoDisplayE3DSharingDashboard } from '../../actions/app/e3dSharingDashboard.actions'
import aveStringSelector from '../../selectors/aveStringSelector'
import isAutoDisplay3dExhibitionEnabledSelector
    from '../../selectors/feature-scope/isAutoDisplay3dExhibitionEnabledSelector'

let callbackHandled = false
let storageListenerAdded = false

const REDIRECT_URL_STORAGE_KEY = 'e3d_auth_redirect_url'

const storeOriginalUrl = (): void => {
    try {
        const currentUrl = window.location.pathname + window.location.search + window.location.hash
        sessionStorage.setItem(REDIRECT_URL_STORAGE_KEY, currentUrl)
        logger.info(`Stored original URL for post-auth redirect: ${currentUrl}`)
    } catch (error) {
        logger.error(`Error storing original URL: ${error}`)
    }
}

const getStoredUrl = (): string | null => {
    try {
        return sessionStorage.getItem(REDIRECT_URL_STORAGE_KEY)
    } catch (error) {
        logger.error(`Error retrieving stored URL: ${error}`)
        return null
    }
}

const clearStoredUrl = (): void => {
    try {
        sessionStorage.removeItem(REDIRECT_URL_STORAGE_KEY)
    } catch (error) {
        logger.error(`Error clearing stored URL: ${error}`)
    }
}

const redirectToStoredUrl = (): void => {
    const storedUrl = getStoredUrl()
    if (storedUrl) {
        clearStoredUrl()
        logger.info(`Redirecting to stored URL: ${storedUrl}`)

        setTimeout(() => {
            history.push(storedUrl)
        }, 100)
    }
}

const hasOAuthCallbackParams = (): boolean => {
    return window?.location?.href && window.location.href.includes('code=') && window.location.href.includes('state=')
}

export const cleanOAuthParamsFromUrl = (): void => {
    if (hasOAuthCallbackParams()) {
        const url = new URL(window.location.href)
        const baseUrl = `${url.origin}${url.pathname}`

        const params = new URLSearchParams(url.search)
        params.delete('code')
        params.delete('state')

        const newUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
        window.history.replaceState({}, document.title, newUrl)
    }
}

const isInIframe = (): boolean => {
    try {
        return window.self !== window.top
    } catch {
        // Cross-origin access to window.top throws
        return true
    }
}

const isPopupContext = (): boolean => {
    return !!window.opener && hasOAuthCallbackParams()
}

/**
 * Called inside the popup window after the IDP redirects back.
 * Tries to notify the opener via signinPopupCallback().
 * If that fails (e.g. window.opener nullified or postMessage blocked),
 * falls back to processing the callback locally with signinRedirectCallback(),
 * stores tokens in localStorage (so the iframe can detect them),
 * and closes the popup.
 */
const handleOAuthPopupCallback = (dispatch): void => {
    const oAuthClient = getOAuthClient()

    if (!oAuthClient) {
        logger.error('Auth client not available for popup callback')
        setTimeout(() => { if (!window.closed) {window.close()} }, 1000)
        return
    }

    oAuthClient.signinPopupCallback()
        .then(() => {
            logger.info('Popup callback posted message to opener successfully')
            // Opener should close us via its PopupWindow handler.
            // If it doesn't within 2s, close ourselves as a safety net.
            setTimeout(() => {
                if (!window.closed) {
                    logger.info('Popup still open after callback; closing manually')
                    window.close()
                }
            }, 2000)
        })
        .catch((error) => {
            logger.error(`Popup callback failed (${error.message || error}), trying redirect fallback`)

            // Fallback: process the callback locally in the popup.
            // This exchanges the code for tokens and stores them in localStorage
            // (shared with the iframe), so the iframe can detect auth via storage events.
            oAuthClient.signinRedirectCallback()
                .then((user) => {
                    logger.info('Redirect callback fallback succeeded in popup')
                    dispatch(loginAsync.success(user))
                    setTimeout(() => {
                        if (!window.closed) {window.close()}
                    }, 500)
                })
                .catch((fallbackError) => {
                    logger.error(`Redirect callback fallback also failed: ${fallbackError}`)
                    dispatch(loginAsync.failure(new Error(String(fallbackError))))
                    setTimeout(() => {
                        if (!window.closed) {window.close()}
                    }, 2000)
                })
        })
}

const handleOAuthRedirectCallback = (dispatch): void => {
    const oAuthClient = getOAuthClient()

    if (!oAuthClient) {
        dispatch(loginAsync.failure(new Error('Auth client not available')))
        return
    }

    dispatch(loginAsync.request())

    oAuthClient
        .signinRedirectCallback()
        .then((loggedInUser) => {
            dispatch(loginAsync.success(loggedInUser))
        })
        .catch((error) => {
            logger.error(`Error during GRP auth callback: ${error}`)
            dispatch(loginAsync.failure(new Error(String(error))))
        })
}

const setupStorageListener = (dispatch): void => {
    if (storageListenerAdded || typeof window === 'undefined') { return }
    storageListenerAdded = true

    window.addEventListener('storage', (event) => {
        if (event.key === 'dcc-e3d-grp-auth' && event.newValue) {
            logger.info('Auth state changed in another window, syncing...')
            try {
                const storedState = getPersistedE3DGrpAuth()
                if (storedState?.user && storedState.isLoggedIn) {
                    const user = new User(storedState.user)
                    logger.info('Detected completed auth in another window, dispatching login success')
                    dispatch(loginAsync.success(user))
                }
            } catch (error) {
                logger.error(`Error syncing auth state from storage event: ${error}`)
            }
        }
    })
}

const e3dGrpAuthMiddleware: Middleware = (store) => {
    const { dispatch, getState } = store

    setupStorageListener(dispatch)

    return (next) => (action) => {
        const result = next(action)

        const is3DExhibitionEnabled = is3DExhibitionEnabledSelector(getState())
        if (!is3DExhibitionEnabled) {
            return result
        }

        if (!callbackHandled && hasOAuthCallbackParams()) {
            callbackHandled = true
            if (isInIframe() && isPopupContext()) {
                handleOAuthPopupCallback(dispatch)
            } else {
                handleOAuthRedirectCallback(dispatch)
            }
        }

        if (isActionOf(loginStart, action)) {
            const oAuthClient = getOAuthClient()

            if (!oAuthClient) {
                dispatch(loginAsync.failure(new Error('Auth client not available')))
                dispatch(
                    createNotification({
                        type: 'error',
                        message: i18n.t('e3d.authNotAvailable'),
                    }),
                )
                return result
            }

            dispatch(loginAsync.request())

            if (isInIframe()) {
                oAuthClient.signinPopup()
                    .then((loggedInUser) => {
                        dispatch(loginAsync.success(loggedInUser))
                    })
                    .catch((error) => {
                        logger.error(`Error during GRP auth popup: ${error}`)
                        if (isAuthenticated()) {
                            logger.info('Popup failed but auth tokens found in storage, syncing state')
                            const storedState = getPersistedE3DGrpAuth()
                            if (storedState?.user) {
                                const user = new User(storedState.user)
                                dispatch(loginAsync.success(user))
                                return
                            }
                        }
                        dispatch(loginAsync.failure(new Error('Failed to initiate login')))
                        dispatch(
                            createNotification({
                                type: 'error',
                                message: i18n.t('e3d.loginFail'),
                            }),
                        )
                    })
            } else {
                storeOriginalUrl()

                oAuthClient.signinRedirect().catch((error) => {
                    logger.error(`Error during GRP auth redirect: ${error}`)
                    // Clear stored URL on error
                    clearStoredUrl()
                    dispatch(loginAsync.failure(new Error('Failed to initiate login')))
                    dispatch(
                        createNotification({
                            type: 'error',
                            message: i18n.t('e3d.loginFail'),
                        }),
                    )
                })
            }
        }

        if (isActionOf(loginAsync.success, action)) {
            cleanOAuthParamsFromUrl()

            const currentAuthState = getState().e3DGrpAuth
            persistE3DGrpAuth(currentAuthState)

            try {
                const user = action.payload
                if (user) {
                    setAuthToken(user)
                }
            } catch (error) {
                logger.error(`Error storing auth tokens: ${error}`)
            }

            dispatch(
                createNotification({
                    type: 'info',
                    message: i18n.t('e3d.loginSuccess'),
                }),
            )

            const state = getState()
            const entryData = entryDataSelector(state)
            const sessionId = sessionIdSelector(state)

            if (!sessionId || !entryData) {
                logger.info('Entry data or session missing after authentication, fetching entry data')
                dispatch(fetchEntryData())
            } else {
                logger.info('Entry data and session already exist, skipping fetch')
                dispatch(fetchCarlineData())
            }

            if (is3DExhibitionEnabled) {
                setTimeout(() => {
                    if ((window as any).__e3dReinitCallback) {
                        logger.info('Triggering E3D reinit after authentication')
                            ; (window as any).__e3dReinitCallback()
                    }
                }, 50)
            }

            // Auto-display E3D Sharing Dashboard if scope is enabled and aveString is present
            const isAutoDisplayEnabled = isAutoDisplay3dExhibitionEnabledSelector(getState())
            if (isAutoDisplayEnabled && is3DExhibitionEnabled) {
                const aveString = aveStringSelector(getState())

                if (aveString) {
                    // aveString is already available, show dashboard immediately
                    logger.info('Auto-displaying E3D Sharing Dashboard after successful login (aveString available)')
                    dispatch(showE3DSharingDashboard())
                } else {
                    // aveString not yet available, set flag to show when it becomes available
                    logger.info('Setting flag to auto-display E3D Sharing Dashboard when aveString becomes available')
                    dispatch(setShouldAutoDisplayE3DSharingDashboard(true))
                }
            }

            // Redirect to the original URL after successful authentication
            redirectToStoredUrl()
        }

        if (isActionOf(loginAsync.failure, action)) {
            cleanOAuthParamsFromUrl()
            // Clear stored URL on authentication failure
            clearStoredUrl()
        }

        if (isActionOf(logout, action)) {
            callbackHandled = false
            clearPersistedE3DGrpAuth()
            // Clear any stored redirect URL on logout
            clearStoredUrl()

            authServiceLogout().catch((error) => {
                logger.error(`Error during logout: ${error}`)
            })

            logger.info('Logout process initiated')
        }

        return result
    }
}

export default e3dGrpAuthMiddleware
